import { IsOEPPhase, type OEPPhase, type OEPSeasonStage } from '../workflow/phases.js';

export type OEPTimelineDateKey =
    | 'ApplicationsClose' | 'MatchingRun' | 'UploadOpens' | 'DraftDue' | 'ReviewOpens' | 'ReviewWindow' | 'FeedbackDue'
    | 'FeedbackRelease' | 'NominatorAccess' | 'OfficeHours' | 'FinalDue' | 'Meeting' | 'NominationsClose' | 'SelectionDue'
    | 'WinnersAnnounced' | 'LinkExpires';

export const OEP_TIMELINE_DATE_KEYS: readonly OEPTimelineDateKey[] = [
    'ApplicationsClose', 'MatchingRun', 'UploadOpens', 'DraftDue', 'ReviewOpens', 'ReviewWindow', 'FeedbackDue',
    'FeedbackRelease', 'NominatorAccess', 'OfficeHours', 'FinalDue', 'Meeting', 'NominationsClose', 'SelectionDue',
    'WinnersAnnounced', 'LinkExpires',
];
const STAGES: readonly OEPSeasonStage[] = ['sum', 'nov', 'dec', 'jan'];

/** A display date: Label is the copy shown to people; On is YYYY-MM-DD (US Eastern calendar date) for sorting. */
export interface IOEPTimelineDate { Label: string; On: string }
export interface IOEPTimelineStage { Label: string; Months: string; Deadline: string; Note: string }
export interface IOEPTimelineMilestone { Label: string; Note: string; Stage: OEPSeasonStage; Phase: OEPPhase }

/** Season.Timeline JSON. Display copy only — no date opens or closes anything (spec D4). */
export interface IOEPSeasonTimeline {
    Dates: Record<OEPTimelineDateKey, IOEPTimelineDate>;
    Stages: Record<OEPSeasonStage, IOEPTimelineStage>;
    Milestones: IOEPTimelineMilestone[];
}

export type ParseResult<T> = { Ok: true; Value: T } | { Ok: false; Error: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === 'string';

export function ParseSeasonTimeline(raw: string | null): ParseResult<IOEPSeasonTimeline> {
    if (!raw || raw.trim().length === 0) return { Ok: false, Error: 'Timeline is empty' };
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return { Ok: false, Error: 'Timeline is not valid JSON' }; }
    if (!isRecord(parsed) || !isRecord(parsed['Dates']) || !isRecord(parsed['Stages']) || !Array.isArray(parsed['Milestones'])) {
        return { Ok: false, Error: 'Timeline must have Dates, Stages and Milestones' };
    }
    const dates = parsed['Dates'];
    for (const key of OEP_TIMELINE_DATE_KEYS) {
        const d = dates[key];
        if (!isRecord(d)) return { Ok: false, Error: `Timeline.Dates.${key} is missing` };
        if (!isString(d['Label']) || !isString(d['On']) || !ISO_DATE.test(d['On'])) {
            return { Ok: false, Error: `Timeline.Dates.${key} needs Label and On (YYYY-MM-DD)` };
        }
    }
    const stages = parsed['Stages'];
    for (const s of STAGES) {
        const st = stages[s];
        if (!isRecord(st) || !['Label', 'Months', 'Deadline', 'Note'].every((k) => isString(st[k]))) {
            return { Ok: false, Error: `Timeline.Stages.${s} needs Label, Months, Deadline and Note` };
        }
    }
    for (const m of parsed['Milestones']) {
        if (!isRecord(m) || !isString(m['Label']) || !isString(m['Note']) || !STAGES.includes(m['Stage'] as OEPSeasonStage) || !isString(m['Phase']) || !IsOEPPhase(m['Phase'])) {
            return { Ok: false, Error: 'Timeline.Milestones entries need Label, Note, Stage and Phase' };
        }
    }
    return { Ok: true, Value: parsed as unknown as IOEPSeasonTimeline };
}

const ET_PARTS = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

/** Offset (ms) of America/New_York from UTC at `instant`: NY wall-clock read as UTC, minus the instant. */
function newYorkOffsetMs(instant: number): number {
    const p = Object.fromEntries(ET_PARTS.formatToParts(new Date(instant)).map((x) => [x.type, x.value]));
    const wall = Date.UTC(Number(p['year']), Number(p['month']) - 1, Number(p['day']), Number(p['hour']), Number(p['minute']), Number(p['second']));
    return wall - instant;
}

/**
 * 23:59:59 US Eastern on a timeline `On` date (YYYY-MM-DD), with the offset actually in force at that
 * moment (EST or EDT). DST switches happen at 2 a.m., so the offset at 23:59 of the day is stable.
 */
export function OEPEndOfDayET(on: string): Date {
    const m = ISO_DATE_PARTS.exec(on);
    if (!m) throw new Error(`Expected a YYYY-MM-DD date, got "${on}"`);
    const wallAsUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
    const firstGuess = wallAsUtc - newYorkOffsetMs(wallAsUtc);
    return new Date(wallAsUtc - newYorkOffsetMs(firstGuess));
}
const ISO_DATE_PARTS = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Season-end expiry for every OEP sign-in link: end of Timeline.Dates.LinkExpires.On, US Eastern. */
export function OEPLinkExpiry(timeline: IOEPSeasonTimeline): Date {
    return OEPEndOfDayET(timeline.Dates.LinkExpires.On);
}
